import { takeLatest, takeEvery } from "redux-saga/effects";
import { schema } from "normalizr";
import {
  defaultReturn
  , makePayload
  , generateDefaultActions
  , requestSaga
  , subKeySaga
  , fillDictionary
  , handleCallback
  , makeReadPayload
} from "shared-utility/store/utils/helpers";
import {
  ToastSuccess,
  ToastError
} from "components/Global/Toasts/Toasty";

const initialState = {
  configItemByParent: {},
  configItemsByParents: {},
  configItemActions: []
};

export const CONFIG_ITEMS_KEY = "configItems";
export const CONFIG_ITEMS_ENTITY = new schema.Entity(CONFIG_ITEMS_KEY, {});
export const CONFIG_ITEMS_SCHEMA = [CONFIG_ITEMS_ENTITY];

// public functions
export const GET_CONFIG_ITEM_BY_PARENT = generateDefaultActions("GET_CONFIG_ITEM_BY_PARENT");
export const getConfigItemByParentId = payload => {
  const configItems = !payload.refresh ? localStorage.getItem(payload.id) : undefined;
  if (configItems) {
    return GET_CONFIG_ITEM_BY_PARENT.createSuccess(JSON.parse(configItems));
  }

  return makeReadPayload(GET_CONFIG_ITEM_BY_PARENT.value, payload);
};
function* getConfigItemByParentIdSaga(payload) {
  yield requestSaga(GET_CONFIG_ITEM_BY_PARENT, `/configItems/byParent/${payload.id}`);
}

export const GET_CONFIG_ITEMS_BY_PARENTS = generateDefaultActions("GET_CONFIG_ITEMS_BY_PARENTS");
export const getConfigItemsByParents = payload => makePayload(GET_CONFIG_ITEMS_BY_PARENTS.value, payload);
function* getConfigItemsByParentsSaga(payload) {
  const url = "/configItemsByParents/";
  const requestSaga = subKeySaga(payload.subKey);
  yield requestSaga(GET_CONFIG_ITEMS_BY_PARENTS, url, "POST", payload);
}

export const CREATE_CONFIG_ITEM = generateDefaultActions("CREATE_CONFIG_ITEM");
export const createConfigItem = payload => makePayload(CREATE_CONFIG_ITEM.value, payload);
function* createConfigItemSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/configItems";
  yield requestSaga(CREATE_CONFIG_ITEM, url, "POST", payload);
  yield callback();
}

export const UPDATE_CONFIG_ITEM = generateDefaultActions("UPDATE_CONFIG_ITEM");
export const updateConfigItem = payload => makePayload(UPDATE_CONFIG_ITEM.value, payload);
function* updateConfigItemSaga(payload) {
  const callback = handleCallback(payload);
  const url = `/configItems/${payload.item.id}`;
  yield requestSaga(UPDATE_CONFIG_ITEM, url, "PUT", payload);
  yield callback();
}
export const DELETE_CONFIG_ITEM = generateDefaultActions("DELETE_CONFIG_ITEM");
export const deleteConfigItem = payload => makePayload(DELETE_CONFIG_ITEM.value, payload);
function* deleteConfigItemSaga(payload) {
  const callback = handleCallback(payload);
  const url = `/configItems/${payload.id}`;
  yield requestSaga(DELETE_CONFIG_ITEM, url, "DELETE");
  yield callback();
}

export const GET_CONFIG_ITEM_ACTIONS = generateDefaultActions("GET_CONFIG_ITEM_ACTIONS");
export const getConfigItemActions = payload => makePayload(GET_CONFIG_ITEM_ACTIONS.value, payload);
function* getConfigItemActionsSaga(payload) {
  const url = `/configItemActions/${payload.companyId}`;
  yield requestSaga(GET_CONFIG_ITEM_ACTIONS, url, "GET");
}

export const CREATE_CONFIG_ITEM_ACTION = generateDefaultActions("CREATE_CONFIG_ITEM_ACTION");
export const createConfigItemAction = payload => makePayload(CREATE_CONFIG_ITEM_ACTION.value, payload);
function* createConfigItemActionSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/configItemActions/new";
  yield requestSaga(CREATE_CONFIG_ITEM_ACTION, url, "POST", payload);
  yield callback();
}

export const UPDATE_CONFIG_ITEM_ACTION = generateDefaultActions("UPDATE_CONFIG_ITEM_ACTION");
export const updateConfigItemAction = payload => makePayload(UPDATE_CONFIG_ITEM_ACTION.value, payload);
function* updateConfigItemActionSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/configItemActions";
  yield requestSaga(UPDATE_CONFIG_ITEM_ACTION, url, "PUT", payload);
  yield callback();
}

export const DELETE_CONFIG_ITEM_ACTION = generateDefaultActions("DELETE_CONFIG_ITEM_ACTION");
export const deleteConfigItemAction = payload => makePayload(DELETE_CONFIG_ITEM_ACTION.value, payload);
function* deleteConfigItemActionSaga(payload) {
  const callback = handleCallback(payload);
  const url = "/configItemActions/delete";
  yield requestSaga(DELETE_CONFIG_ITEM_ACTION, url, "DELETE", payload);
  yield callback();
}

// Reducer
export default function configItemsReducer(state = initialState, action) {
  const { type, payload, subKey } = action;
  switch (type) {
    case GET_CONFIG_ITEM_BY_PARENT.success:
      const key = payload[0]?.parentId;
      const configItems = fillDictionary(state, payload, "configItemByParent", key);
      localStorage.setItem(key, JSON.stringify(configItems.configItemByParent[key]));

      return configItems;

    case GET_CONFIG_ITEMS_BY_PARENTS.success:
      return fillDictionary(state, payload, "configItemsByParents", subKey);

    case CREATE_CONFIG_ITEM.success:
      ToastSuccess("Successfully Added");

      return defaultReturn(state);

    case UPDATE_CONFIG_ITEM.success:
      ToastSuccess("Successfully Changed");

      return defaultReturn(state);

    case DELETE_CONFIG_ITEM.success:
      ToastSuccess("Successfully Deleted");

      return defaultReturn(state);

    case GET_CONFIG_ITEM_ACTIONS.success:
      return defaultReturn(state, payload, "configItemActions");

    case GET_CONFIG_ITEM_ACTIONS.failure:
      ToastError("Failed getting config item actions");

      return defaultReturn(state);

    case CREATE_CONFIG_ITEM_ACTION.success:
      return defaultReturn(state);

    case CREATE_CONFIG_ITEM_ACTION.failure:
      ToastError("Failed creating system action");

      return defaultReturn(state);

    case UPDATE_CONFIG_ITEM_ACTION.success:
      return defaultReturn(state);

    case UPDATE_CONFIG_ITEM_ACTION.failure:
      ToastError("Failed updating system action");

      return defaultReturn(state);

    case DELETE_CONFIG_ITEM_ACTION.success:
      return defaultReturn(state);

    case DELETE_CONFIG_ITEM_ACTION.failure:
      ToastError("Failed deleting system action");

      return defaultReturn(state);

    default:
      return state;
  }
}

// Saga
export function* configItemsSaga() {
  yield takeEvery(GET_CONFIG_ITEM_BY_PARENT.value, getConfigItemByParentIdSaga);
  yield takeLatest(GET_CONFIG_ITEMS_BY_PARENTS.value, getConfigItemsByParentsSaga);
  yield takeLatest(DELETE_CONFIG_ITEM.value, deleteConfigItemSaga);
  yield takeLatest(CREATE_CONFIG_ITEM.value, createConfigItemSaga);
  yield takeLatest(UPDATE_CONFIG_ITEM.value, updateConfigItemSaga);
  yield takeLatest(GET_CONFIG_ITEM_ACTIONS.value, getConfigItemActionsSaga);
  yield takeLatest(CREATE_CONFIG_ITEM_ACTION.value, createConfigItemActionSaga);
  yield takeLatest(UPDATE_CONFIG_ITEM_ACTION.value, updateConfigItemActionSaga);
  yield takeLatest(DELETE_CONFIG_ITEM_ACTION.value, deleteConfigItemActionSaga);
}
